import React, { useEffect, useState } from "react";
import { PhoneCall, Phone, RefreshCw, X, Search, Edit2, Save, Trash2, Plus } from "lucide-react";
import { getOutboundCallUrl, getCurrentUserIdForCalls } from "../lib/apiConfig.js";
import { useUserPhoneNumbers } from "../hooks/useUserPhoneNumbers.js";
import TestNumberBadge from "../components/TestNumberBadge.js";
import { TEST_PHONE_NUMBER } from "../lib/phoneUtils.js";

// Use relative URLs for API calls to work with nginx proxy
const getApiBase = () => {
  const agentApiUrl = process.env.REACT_APP_AGENT_API_URL || '';
  if (agentApiUrl && (agentApiUrl.startsWith('http://') || agentApiUrl.startsWith('https://'))) {
    return agentApiUrl;
  }
  return '';
};

const API_BASE = getApiBase();
const SCRIPTS_ENDPOINT = `${API_BASE}/api/scripts`;

// Sample contacts stored in frontend
const INITIAL_CONTACTS = [
  { id: '1', firstName: 'John', lastName: 'Doe', phone: '+18055551234', birthdate: '1985-03-15' },
  { id: '2', firstName: 'Jane', lastName: 'Smith', phone: '+18055555678', birthdate: '1990-07-22' },
  { id: '3', firstName: 'Michael', lastName: 'Johnson', phone: '+18055559012', birthdate: '1988-11-05' },
  { id: '4', firstName: 'Sarah', lastName: 'Williams', phone: '+18055553456', birthdate: '1992-02-18' },
  { id: '5', firstName: 'David', lastName: 'Brown', phone: '+18055557890', birthdate: '1987-09-30' },
];

function formatDate(v) {
  if (!v) return '—';
  try {
    const d = new Date(v);
    return d.toLocaleDateString();
  } catch {
    return '—';
  }
}

export default function BatchCall() {
  const { allNumbers } = useUserPhoneNumbers();
  // Load contacts from localStorage or use initial contacts
  const loadContactsFromStorage = () => {
    try {
      const saved = localStorage.getItem('batchCallContacts');
      return saved ? JSON.parse(saved) : INITIAL_CONTACTS;
    } catch {
      return INITIAL_CONTACTS;
    }
  };

  const [contacts, setContacts] = useState(loadContactsFromStorage);
  const [scripts, setScripts] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(25);
  
  // Edit state
  const [editingId, setEditingId] = useState(null);
  const [editForm, setEditForm] = useState({ firstName: '', lastName: '', phone: '', birthdate: '' });
  const [showAddModal, setShowAddModal] = useState(false);
  const [newContact, setNewContact] = useState({ firstName: '', lastName: '', phone: '', birthdate: '' });
  
  // Call modal state
  const [callModalOpen, setCallModalOpen] = useState(false);
  const [selectedContact, setSelectedContact] = useState(null);
  const [selectedScriptId, setSelectedScriptId] = useState('');
  const [callingNumber, setCallingNumber] = useState(TEST_PHONE_NUMBER);
  const [callStack, setCallStack] = useState("kokoro-vad");
  const [calling, setCalling] = useState(false);
  
  // Override fields
  const [overrideBusinessName, setOverrideBusinessName] = useState("");
  const [overrideAiName, setOverrideAiName] = useState("");
  
  // Greeting/intro message state
  const [greetingMessage, setGreetingMessage] = useState("");
  const [greetingEdited, setGreetingEdited] = useState(false);
  const [selectedScriptData, setSelectedScriptData] = useState(null);

  async function fetchJson(url, opts) {
    const token = localStorage.getItem('token');
    const res = await fetch(url, {
      mode: 'cors',
      headers: {
        'Content-Type': 'application/json',
        ...(token ? { 'Authorization': `Bearer ${token}` } : {}),
        ...(opts?.headers || {})
      },
      ...(opts || {})
    });
    const text = await res.text();
    let json = null;
    try {
      json = text ? JSON.parse(text) : null;
    } catch {}
    if (!res.ok || json?.success === false) {
      const msg = json?.message || text || `${res.status} ${res.statusText}`;
      throw new Error(msg);
    }
    return json ?? {};
  }

  // Save contacts to localStorage whenever they change
  useEffect(() => {
    localStorage.setItem('batchCallContacts', JSON.stringify(contacts));
  }, [contacts]);

  // Filter contacts based on search term
  const filteredContacts = contacts.filter(contact => {
    if (!searchTerm) return true;
    const search = searchTerm.toLowerCase();
    return (
      (contact.firstName || '').toLowerCase().includes(search) ||
      (contact.lastName || '').toLowerCase().includes(search) ||
      (contact.phone || '').toLowerCase().includes(search) ||
      (contact.birthdate || '').toLowerCase().includes(search)
    );
  });

  // Pagination
  const total = filteredContacts.length;
  const paginatedContacts = filteredContacts.slice((page - 1) * limit, page * limit);
  const hasMore = page * limit < total;

  // Edit functions
  const startEdit = (contact) => {
    setEditingId(contact.id);
    setEditForm({
      firstName: contact.firstName || '',
      lastName: contact.lastName || '',
      phone: contact.phone || '',
      birthdate: contact.birthdate || '',
    });
  };

  const cancelEdit = () => {
    setEditingId(null);
    setEditForm({ firstName: '', lastName: '', phone: '', birthdate: '' });
  };

  const saveEdit = () => {
    if (!editForm.firstName && !editForm.lastName) {
      alert('First name or last name is required');
      return;
    }
    setContacts(contacts.map(contact => 
      contact.id === editingId 
        ? { ...contact, ...editForm }
        : contact
    ));
    cancelEdit();
  };

  const deleteContact = (id) => {
    if (window.confirm('Are you sure you want to delete this contact?')) {
      setContacts(contacts.filter(contact => contact.id !== id));
    }
  };

  const addContact = () => {
    if (!newContact.firstName && !newContact.lastName) {
      alert('First name or last name is required');
      return;
    }
    if (!newContact.phone) {
      alert('Phone number is required');
      return;
    }
    const newId = String(Date.now());
    setContacts([...contacts, { ...newContact, id: newId }]);
    setNewContact({ firstName: '', lastName: '', phone: '', birthdate: '' });
    setShowAddModal(false);
  };

  const loadScripts = async () => {
    try {
      const json = await fetchJson(SCRIPTS_ENDPOINT);
      setScripts(json.scripts || []);
    } catch (e) {
      console.error('Failed to load scripts:', e);
    }
  };

  useEffect(() => {
    loadScripts();
  }, []);

  // Load full script document when a script is selected (like BrowserVoice)
  const loadScriptDetail = async (id) => {
    if (!id) {
      setSelectedScriptData(null);
      return;
    }
    try {
      const token = localStorage.getItem('token');
      const apiBase = process.env.REACT_APP_AGENT_API_URL || '';
      const res = await fetch(`${apiBase}/api/script/${id}`, {
        headers: {
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
      });
      const data = await res.json();
      if (data && data.success) {
        setSelectedScriptData(data.script || null);
      }
    } catch (err) {
      console.error("❌ Error loading script:", err);
      setSelectedScriptData(null);
    }
  };

  // Build greeting message from script data (exactly like BrowserVoice)
  const buildGreetingMessage = (script, overrideFirstName, overrideLastName, overrideBusinessName, overrideAiName) => {
    if (!script) return "";
    // Use overrides if provided, otherwise use script values
    const businessName = overrideBusinessName || (script && (script.businessName || script.scriptName)) || 'our office';
    const aiName = overrideAiName || (script && script.aiName) || 'Maya';
    const firstName = overrideFirstName || (script && script.firstName ? script.firstName : null);
    const lastName = overrideLastName || (script && script.lastName ? script.lastName : null);
    const recipient = firstName ? `${firstName}${lastName ? ' ' + lastName : ''}` : 'you';
    return `Hi there! This is ${aiName}, an AI agent from ${businessName}! Is this ${recipient}?`.trim();
  };

  const handleCallClick = (contact) => {
    setSelectedContact(contact);
    setSelectedScriptId('');
    setOverrideBusinessName("");
    setOverrideAiName("");
    setGreetingMessage("");
    setGreetingEdited(false);
    setSelectedScriptData(null);
    setCallModalOpen(true);
  };
  
  // Load script detail when script ID changes (like BrowserVoice)
  useEffect(() => {
    if (selectedScriptId) {
      setGreetingEdited(false); // allow auto-regeneration on new script
      loadScriptDetail(selectedScriptId);
    } else {
      setSelectedScriptData(null);
    }
  }, [selectedScriptId]);
  
  // Keep greeting synced with script and contact names until user edits greeting manually (like BrowserVoice)
  useEffect(() => {
    if (!greetingEdited && selectedScriptData && selectedContact) {
      setGreetingMessage(buildGreetingMessage(
        selectedScriptData,
        selectedContact.firstName || '',
        selectedContact.lastName || '',
        overrideBusinessName,
        overrideAiName
      ));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedScriptData, selectedContact, overrideBusinessName, overrideAiName]);

  const handleStartCall = async () => {
    if (!selectedContact || !selectedScriptId) {
      alert('Please select a script');
      return;
    }

    if (!selectedContact.phone) {
      alert('Contact does not have a phone number');
      return;
    }

    setCalling(true);
    try {
      const selectedScript = scripts.find(s => s._id === selectedScriptId);
      if (!selectedScript) {
        throw new Error('Script not found');
      }
      const hasSavedGreeting = !!(
        (selectedScriptData?.greetingTemplate && String(selectedScriptData.greetingTemplate).trim()) ||
        (selectedScript?.greetingTemplate && String(selectedScript.greetingTemplate).trim())
      );
      const gm = (greetingMessage || '').trim();

      const endpoint = getOutboundCallUrl();
      const voiceId = callStack === "kokoro-vad" ? "af_heart" : null;
      const token = localStorage.getItem('token');
      const placedByUserId = getCurrentUserIdForCalls();
      console.log("[OutboundCall] POST", endpoint, "| hasToken:", !!token, "| scriptId:", selectedScript?._id);
      const res = await fetch(endpoint, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          ...(token ? { 'Authorization': `Bearer ${token}` } : {})
        },
          body: JSON.stringify({
            to: selectedContact.phone,
            from: callingNumber,
            stack: callStack || 'kokoro-vad',
            ...(placedByUserId ? { placedByUserId } : {}),
            scriptData: {
              ...selectedScript,
              firstName: selectedContact.firstName || null,
              lastName: selectedContact.lastName || null,
              businessName: overrideBusinessName || null,
              aiName: overrideAiName || null,
            },
            collectionIds: (() => {
              const numDoc = allNumbers.find((n) => n.number === callingNumber);
              const ids = Array.isArray(numDoc?.outboundCollectionIds) ? numDoc.outboundCollectionIds : [];
              return ids.length > 0 ? ids : null;
            })(),
            voiceId: voiceId,
            ...(greetingEdited && gm
              ? { firstMessage: gm, firstMessageOverride: true }
              : !hasSavedGreeting && gm
                ? { firstMessage: gm }
                : {}),
          }),
      });

      const raw = await res.text();
      console.log("[OutboundCall] Response", res.status, "| body length:", raw?.length ?? 0);
      if (!res.ok) console.warn("[OutboundCall] Body:", raw?.substring(0, 400));
      let data = null;
      try {
        data = raw ? JSON.parse(raw) : null;
      } catch {
        console.error("[OutboundCall] Not JSON:", raw?.substring(0, 500));
        alert('❌ Server returned an invalid response.');
        return;
      }
      if (res.ok && data?.success) {
        alert(`📞 Call started to ${selectedContact.firstName} ${selectedContact.lastName}!\nCall SID: ${data.callSid}`);
        setCallModalOpen(false);
      } else {
        alert(`❌ Failed: ${data?.message || "Unknown error"}`);
      }
    } catch (err) {
      console.error("❌ Error starting call:", err);
      alert(`❌ Error starting call: ${err.message}`);
    } finally {
      setCalling(false);
    }
  };

	return (
    <div className="relative isolate h-full min-h-0 overflow-auto">
      <div className="pointer-events-none absolute inset-0 bg-gradient-to-b from-[#eef3ff] via-white to-white" />
      <div className="relative p-4 sm:p-6">
        <div className="flex items-center gap-3 mb-6">
          <div className="w-10 h-10 rounded-xl bg-white ring-1 ring-black/5 shadow flex items-center justify-center text-gray-900">
            <PhoneCall className="w-5 h-5" />
          </div>
          <h1 className="text-xl md:text-2xl font-semibold bg-gradient-to-r from-gray-900 via-gray-800 to-gray-900 bg-clip-text text-transparent">
            Batch Call
          </h1>
        </div>

        {/* Search and Add Contact */}
        <div className="mb-6 flex gap-4 items-center">
          <div className="flex-1 relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
            <input
              type="text"
              placeholder="Search contacts by name, phone, or birthdate..."
              className="w-full pl-10 pr-4 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
              value={searchTerm}
              onChange={(e) => {
                setSearchTerm(e.target.value);
                setPage(1);
              }}
            />
          </div>
          <button
            onClick={() => setShowAddModal(true)}
            className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 flex items-center gap-2"
          >
            <Plus className="w-4 h-4" />
            Add Contact
          </button>
        </div>

        {/* CRM Table */}
        <div className="bg-white rounded-lg shadow-md overflow-hidden">
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    First Name
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Last Name
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Phone Number
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Birthdate
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {paginatedContacts.length === 0 && (
                  <tr>
                    <td colSpan={5} className="px-6 py-8 text-center text-gray-500">
                      {searchTerm ? 'No contacts match your search' : 'No contacts found'}
                    </td>
                  </tr>
                )}
                {paginatedContacts.map((contact) => (
                  <tr key={contact.id} className="hover:bg-gray-50">
                    {editingId === contact.id ? (
                      <>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <input
                            type="text"
                            value={editForm.firstName}
                            onChange={(e) => setEditForm({ ...editForm, firstName: e.target.value })}
                            className="w-full px-2 py-1 border rounded text-sm"
                            placeholder="First Name"
                          />
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <input
                            type="text"
                            value={editForm.lastName}
                            onChange={(e) => setEditForm({ ...editForm, lastName: e.target.value })}
                            className="w-full px-2 py-1 border rounded text-sm"
                            placeholder="Last Name"
                          />
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <input
                            type="text"
                            value={editForm.phone}
                            onChange={(e) => setEditForm({ ...editForm, phone: e.target.value })}
                            className="w-full px-2 py-1 border rounded text-sm"
                            placeholder="Phone"
                          />
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <input
                            type="date"
                            value={editForm.birthdate}
                            onChange={(e) => setEditForm({ ...editForm, birthdate: e.target.value })}
                            className="w-full px-2 py-1 border rounded text-sm"
                          />
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm">
                          <div className="flex items-center gap-2">
                            <button
                              onClick={saveEdit}
                              className="inline-flex items-center gap-1 px-3 py-1 bg-green-600 text-white rounded hover:bg-green-700"
                            >
                              <Save className="w-3 h-3" />
                              Save
                            </button>
                            <button
                              onClick={cancelEdit}
                              className="inline-flex items-center gap-1 px-3 py-1 bg-gray-600 text-white rounded hover:bg-gray-700"
                            >
                              <X className="w-3 h-3" />
                              Cancel
                            </button>
                          </div>
                        </td>
                      </>
                    ) : (
                      <>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                          {contact.firstName || '—'}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                          {contact.lastName || '—'}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                          {contact.phone ? (
                            <span className="inline-flex items-center gap-1">
                              <Phone className="w-4 h-4" />
                              {contact.phone}
                            </span>
                          ) : (
                            '—'
                          )}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                          {contact.birthdate ? formatDate(contact.birthdate) : '—'}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm">
                          <div className="flex items-center gap-2">
                            <button
                              onClick={() => handleCallClick(contact)}
                              disabled={!contact.phone}
                              className="inline-flex items-center gap-2 px-3 py-1.5 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition text-sm"
                            >
                              <PhoneCall className="w-4 h-4" />
                              Call
                            </button>
                            <button
                              onClick={() => startEdit(contact)}
                              className="inline-flex items-center gap-1 px-3 py-1.5 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 transition"
                              title="Edit"
                            >
                              <Edit2 className="w-4 h-4" />
                            </button>
                            <button
                              onClick={() => deleteContact(contact.id)}
                              className="inline-flex items-center gap-1 px-3 py-1.5 bg-red-100 text-red-700 rounded-lg hover:bg-red-200 transition"
                              title="Delete"
                            >
                              <Trash2 className="w-4 h-4" />
                            </button>
                          </div>
                        </td>
                      </>
                    )}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Pagination */}
          <div className="bg-gray-50 px-6 py-4 flex items-center justify-between border-t border-gray-200">
            <div className="text-sm text-gray-700">
              Showing <span className="font-medium">{paginatedContacts.length}</span> of{' '}
              <span className="font-medium">{total}</span> contacts
            </div>
            <div className="flex items-center gap-4">
              <select
                value={limit}
                onChange={(e) => {
                  setLimit(Number(e.target.value));
                  setPage(1);
                }}
                className="px-3 py-1.5 border rounded-lg bg-white text-sm"
              >
                {[10, 25, 50, 100].map((n) => (
                  <option key={n} value={n}>
                    {n} / page
                  </option>
                ))}
              </select>
              <div className="flex items-center gap-2">
                <button
                  className="px-3 py-1.5 border rounded-lg bg-white text-sm disabled:opacity-50 hover:bg-gray-50"
                  onClick={() => setPage((p) => Math.max(1, p - 1))}
                  disabled={page === 1}
                >
                  Prev
                </button>
                <div className="text-sm">
                  Page <span className="font-semibold">{page}</span>
                </div>
                <button
                  className="px-3 py-1.5 border rounded-lg bg-white text-sm disabled:opacity-50 hover:bg-gray-50"
                  onClick={() => setPage((p) => (hasMore ? p + 1 : p))}
                  disabled={!hasMore}
                >
                  Next
                </button>
              </div>
            </div>
          </div>
        </div>

        {error && (
          <div className="mt-4 p-4 bg-red-50 border border-red-200 rounded-lg text-red-800">
            {error}
          </div>
        )}
      </div>

      {/* Add Contact Modal */}
      {showAddModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
          <div className="absolute inset-0 bg-black/30" onClick={() => setShowAddModal(false)} />
          <div className="relative bg-white rounded-lg shadow-xl max-w-md w-full p-6">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-xl font-semibold">Add New Contact</h2>
              <button
                onClick={() => setShowAddModal(false)}
                className="p-2 hover:bg-gray-100 rounded-lg"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  First Name
                </label>
                <input
                  type="text"
                  value={newContact.firstName}
                  onChange={(e) => setNewContact({ ...newContact, firstName: e.target.value })}
                  className="w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder="First Name"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Last Name
                </label>
                <input
                  type="text"
                  value={newContact.lastName}
                  onChange={(e) => setNewContact({ ...newContact, lastName: e.target.value })}
                  className="w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder="Last Name"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Phone Number *
                </label>
                <input
                  type="text"
                  value={newContact.phone}
                  onChange={(e) => setNewContact({ ...newContact, phone: e.target.value })}
                  className="w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder="+18055551234"
                  required
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Birthdate
                </label>
                <input
                  type="date"
                  value={newContact.birthdate}
                  onChange={(e) => setNewContact({ ...newContact, birthdate: e.target.value })}
                  className="w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>
              <div className="flex justify-end gap-3 pt-4">
                <button
                  onClick={() => setShowAddModal(false)}
                  className="px-4 py-2 border rounded-lg hover:bg-gray-50"
                >
                  Cancel
                </button>
                <button
                  onClick={addContact}
                  className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
                >
                  Add Contact
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Call Modal */}
      {callModalOpen && selectedContact && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
          <div className="absolute inset-0 bg-black/30" onClick={() => setCallModalOpen(false)} />
          <div className="relative bg-white rounded-lg shadow-xl max-w-2xl w-full p-6">
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-xl font-semibold">
                Call {selectedContact.firstName} {selectedContact.lastName}
              </h2>
              <button
                onClick={() => setCallModalOpen(false)}
                className="p-2 hover:bg-gray-100 rounded-lg"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Phone Number
                </label>
                <div className="px-4 py-2 bg-gray-50 rounded-lg text-gray-900">
                  {selectedContact.phone || '—'}
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Select Script *
                </label>
                <select
                  value={selectedScriptId}
                  onChange={(e) => {
                    setSelectedScriptId(e.target.value);
                    setGreetingEdited(false); // Reset edited flag when script changes
                  }}
                  className="w-full px-4 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                  required
                >
                  <option value="">-- Select a script --</option>
                  {scripts.map((script) => (
                    <option key={script._id} value={script._id}>
                      {script.scriptName}
                    </option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Calling Number
                  {callingNumber && allNumbers.find((n) => n.number === callingNumber)?.isTest && (
                    <span className="ml-2"><TestNumberBadge /></span>
                  )}
                </label>
                <select
                  value={callingNumber || ""}
                  onChange={(e) => setCallingNumber(e.target.value)}
                  className="w-full px-4 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                >
                  {allNumbers.map((item) => (
                    <option key={item.number} value={item.number}>
                      {item.display}
                    </option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Call Stack
                </label>
                <select
                  value={callStack}
                  onChange={(e) => setCallStack(e.target.value)}
                  className="w-full px-4 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                >
                  <option value="kokoro-vad">Kokoro VAD (Recommended)</option>
                  <option value="legacy">Legacy</option>
                </select>
              </div>

              {/* Override fields */}
              {selectedScriptId && (
                <>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Business Name (optional override)
                    </label>
                    <input
                      type="text"
                      placeholder="Leave empty to use script's business name"
                      className="w-full px-4 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                      value={overrideBusinessName}
                      onChange={(e) => setOverrideBusinessName(e.target.value)}
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      AI Name (optional override)
                    </label>
                    <input
                      type="text"
                      placeholder="Leave empty to use script's AI name (e.g., Maya)"
                      className="w-full px-4 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                      value={overrideAiName}
                      onChange={(e) => setOverrideAiName(e.target.value)}
                    />
                  </div>
                </>
              )}

              {/* Greeting/Intro Message */}
              {selectedScriptId && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Greeting Message
                  </label>
                  <textarea
                    placeholder="Enter greeting message..."
                    className="w-full px-4 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                    rows={3}
                    value={greetingMessage}
                    onChange={(e) => {
                      setGreetingMessage(e.target.value);
                      setGreetingEdited(true);
                    }}
                  />
                  <div className="flex items-center justify-between mt-1">
                    {!greetingEdited && greetingMessage && (
                      <p className="text-xs text-gray-500">
                        Auto-generated from script. Edit to customize.
                      </p>
                    )}
                    {greetingEdited && selectedContact && (
                      <button
                        type="button"
                        onClick={() => {
                          setGreetingEdited(false);
                          if (selectedScriptData && selectedContact) {
                            setGreetingMessage(buildGreetingMessage(
                              selectedScriptData,
                              selectedContact.firstName || '',
                              selectedContact.lastName || ''
                            ));
                          }
                        }}
                        className="text-xs text-blue-600 hover:text-blue-800 underline"
                      >
                        Reset to auto-generated
                      </button>
                    )}
                  </div>
                </div>
              )}

              <div className="flex justify-end gap-3 pt-4">
                <button
                  onClick={() => setCallModalOpen(false)}
                  className="px-4 py-2 border rounded-lg hover:bg-gray-50"
                  disabled={calling}
                >
                  Cancel
                </button>
                <button
                  onClick={handleStartCall}
                  disabled={!selectedScriptId || calling}
                  className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
                >
                  {calling ? (
                    <>
                      <RefreshCw className="w-4 h-4 animate-spin" />
                      Calling...
                    </>
                  ) : (
                    <>
                      <PhoneCall className="w-4 h-4" />
                      Start Call
                    </>
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
